/**
 * The last thing okthx said, which is what it is waiting on the reader for.
 *
 * Where it asked questions this is the reason they exist — "I took on booking
 * the offsite venue, but I couldn't find any details about it". Where it asked
 * for something a form cannot take — a browser to drive, an approval, a job to
 * go and do — it is the whole of the request. Its own words either way, rather
 * than a summary built here.
 *
 * Shared because both platforms draw the same line from the same list, and the
 * walk is the sort of thing that gets a subtly different stopping rule the
 * second time it is written.
 */

/** The shape both platforms' message lists hand back, narrowed to what this reads. */
type MessageItem = { type: string; message?: { type: string; content: string } };

/**
 * The types okthx speaks in.
 *
 * `agentQuestion` is the one that matters here and was the one missing: the
 * workflow files an ask under it precisely when the turn asks something
 * (`messageType: asksSomething || question ? "agentQuestion" : …`), so reading
 * only `agentResponse` skipped every real question and left the surfaces that
 * call this showing their own stand-in line instead — "Okthx is waiting on you
 * in this task's conversation", above an input meant to answer a question the
 * reader could not see.
 */
const AGENT_SPEECH: ReadonlySet<string> = new Set(["agentResponse", "agentQuestion"]);

export function lastAgentText(items: readonly MessageItem[]): string | undefined {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (item?.type !== "message") continue;
    if (!item.message || !AGENT_SPEECH.has(item.message.type)) continue;
    const text = item.message.content.trim();
    if (text) return text;
  }
  return undefined;
}
